import React, { useState, useEffect } from "react";
import "./Orders.css";
import OrdersService from "../../services/OrdersService";
import LoadingAnimation from "../Loading/LoadingAnimation";
import ConfirmationModal from "../ConfirmationModal/ConfiramtionModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import EventService from "../../services/EventService";
import { set } from "date-fns";
import moment from "moment";
import DownloadExcelButton from "./DownloadExcel";

const Orders = () => {
  interface Order {
    IdTableOrder: number;
    TableName: string;
    email: string;
    phoneNumber: string;
    seats: number;
    totalCost: number;
    Topic: string;
    FirstName: string;
    LastName: string;
    StartTime: string;
    EndTime: string;
    Cost: number;
  }

  const [orders, setOrders] = useState<Order[]>([]);
  const [orderOffset, setOrderOffset] = useState(0);

  const [events, setEvents] = useState<any[]>([]);
  const [eventOffset, setEventOffset] = useState(0);
  const [selectedEventIndex, setSelectedEvent] = useState<number>(-1);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error" | null>(
    null
  );
  const showSnackbar = (msg: string, type: "success" | "error") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage(null);
      setMessageType(null);
    }, 3000); // Auto-hide after 3 seconds
  };

  useEffect(() => {
    fetchEvents();
  }, [eventOffset]);

  const fetchEvents = async () => {
    try {
      const res = await EventService.getEvents(eventOffset);
      setEvents(res.data.My_Result);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchfile = async (eventId: number) => {
    try {
      const res = await OrdersService.getFileToDownload(eventId);
      console.log(res.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const fetchOrders = async (eventId: number) => {
    try {
      setLoading(true);
      const response: any = await OrdersService.getOrders(eventId);
      if (response.status === 200) {
        const fetchedOrders = response.data.My_Result.flatMap((result: any) =>
          result.TableAvailabilities.flatMap(
            (availability: any) => availability.Orders
          )
        );
        setOrders(fetchedOrders);
      }
    } catch (error: any) {
      if (error.response.status === 404) {
        setOrders([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (order: Order) => {
    setOrderToCancel(order);
    setShowConfirmationModal(true);
  };

  const handleConfirmCancel = async () => {
    setLoading(true);
    if (orderToCancel) {
      try {
        const response = await OrdersService.deleteOrder(
          orderToCancel.IdTableOrder
        );
        if (response.status === 200) {
          showSnackbar("Order cancelled successfully.", "success");
        } else {
          showSnackbar("Failed to cancel order.", "error");
        }
      } catch (error) {
        showSnackbar("Error cancelling order. Please try again.", "error");
      } finally {
        setShowConfirmationModal(false);
        setOrderToCancel(null);
        setLoading(false);
        setTimeout(() => {
          setMessage(null);
          setMessageType(null);
        }, 3000);
      }
    }
  };

  const handleCancelModal = () => {
    setShowConfirmationModal(false);
    setOrderToCancel(null);
  };

  const filteredOrders = orders.filter(
    (order: any) =>
      order.TableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.Topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.PhoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.Cost.toString().includes(searchTerm)
  );

  return (
    <div className="orders-page">
      {message && (
        <div className={`message-bar ${messageType}`}>
          {message}
          <button className="close-btn" onClick={() => setMessage(null)}>
            ×
          </button>
        </div>
      )}

      <header className="orders-header">
        <h1>Table Reservations</h1>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search reservations"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </header>

      <div className="flex flex-row overflow-x-auto whitespace-nowrap mb-2 pb-2">
        {events.map((event, index) => (
          <button
            key={index}
            className={`p-4 hover:bg-gray-200 transition-colors border rounded-2xl ${
              selectedEventIndex === index ? "bg-gray-200" : ""
            }`}
            onClick={() => {
              setSelectedEvent(index);
              fetchOrders(event.IdEvent);
            }}>
            {event.EventName}
            <br />
            {event.Location} <br />
            {moment(event.StartDate).format("MMM DD-YYYY")} -{" "}
            {moment(event.EndDate).format("MMM DD-YYYY")}
          </button>
        ))}

        {/* load more button */}
        {events.length > 0 && events.length % 20 === 0 && (
          <button
            className="p-4 hover:bg-gray-200 transition-colors border rounded-2xl"
            onClick={() => setEventOffset(eventOffset + 20)}>
            Load More
          </button>
        )}
      </div>

      {events[selectedEventIndex] && (
        <DownloadExcelButton eventId={events[selectedEventIndex]?.IdEvent} />
      )}

      {loading ? (
        <LoadingAnimation />
      ) : (
        <table className="orders-table">
          <thead>
            <tr>
              <th></th>
              <th>Table Name</th>
              <th>Topic</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order: any, index: number) => (
              <tr key={index}>
                <td>
                  <button
                    className="delete-button"
                    onClick={() => handleCancelClick(order)}>
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </button>
                </td>
                <td>
                  {order.TableName}
                  <br />({order.Seats} {order.Seats === 1 ? "Seat" : "Seats"}){" "}
                </td>
                <td>
                  {order.Topic}
                  <br />({order.StartTime}-{order.EndTime})
                </td>
                <td>
                  {order.FirstName} {order.LastName}
                  <br />
                  <a href={`tel:${order.PhoneNumber}`}>+{order.PhoneNumber}</a>
                </td>
                <td>{order.Email}</td>
                <td>
                  {events[selectedEventIndex]?.CurrencyCode} {order.Cost}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showConfirmationModal && (
        <ConfirmationModal
          message={`You are about to cancel the order for <strong>${orderToCancel?.TableName}</strong>. Do you want to proceed?`}
          onConfirm={handleConfirmCancel}
          onCancel={handleCancelModal}
        />
      )}
    </div>
  );
};

export default Orders;
